import { contentCatalog } from "../../content/catalog"
import { LONG_GRASS_FIELD, FLORA_TILESET, floraTextureId } from "../flora"
import { tileSetSpecs } from "../base_map_builder"
import { compositeTileParent, entityTileParent, PlantedTileSpec } from "./forest_trees"

/**
 * The ember meadow's long grass: six Lab-made clumps grown by packages/tools/src/trees/grass.py, in
 * place of the LPC sheet's tile 227 (`tallGrass`). Each plays exactly like tallGrass -- 2 hp, straw and
 * seed, regrowing through grassSprout -- and is tagged `grass: "long"`, which is what the wind, the
 * wildlife's cover and the cut sound go by.
 *
 * Planted by ENTITY ID and looked up in the catalog (entityTileParent), because a Lab tile lands in
 * whatever authored-terrain cell was free when it was saved.
 */
export const MEADOW_GRASS = [
    "meadow-grass-a",
    "meadow-grass-b",
    "meadow-grass-c",
    "meadow-grass-d",
    "meadow-grass-e",
    "meadow-grass-f",
]

// A well-mixed hash of a cell. Read from its HIGH bits: a final multiply barely stirs the low ones,
// so `hash % n` hands neighbouring cells the same variant far more often than chance.
const cellHash = (x: number, y: number): number => {
    let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1)
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b)
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35)
    return (h ^ (h >>> 16)) >>> 0
}

/**
 * Which meadow clump grows at (x, y), and where to plant it.
 *
 * Chosen by the cell, not by the builder's random stream, deliberately: drawing one more number per
 * clump would re-deal every seeded camp -- every tree, bush and chicken after the first clump -- when
 * all this was meant to change is the grass's art.
 *
 * Falls back to the LPC long grass if the catalog does not carry the meadow (a content set without
 * it), so a room still grows grass rather than failing to build.
 */
export const meadowGrassAt = (x: number, y: number): PlantedTileSpec => {
    const id = MEADOW_GRASS[Math.floor((cellHash(x, y) / 2 ** 32) * MEADOW_GRASS.length)]
    return entityTileParent(id) ?? { tilesetId: FLORA_TILESET, tilePos: LONG_GRASS_FIELD }
}

/**
 * The meadow's short tufts (grass.py MEADOW_TUFTS), in place of the LPC tufts LONG_GRASS_TUFTS: the
 * clumps' blades and colours on one cell. SCENERY -- no entity tile, nothing to cut -- found by
 * composite id.
 *
 * Exactly as many as LONG_GRASS_TUFTS, so a pick is the same one draw over the same number of choices
 * as `randomOneOf(LONG_GRASS_TUFTS)` was, and a room grows exactly as before; only the art changes.
 */
export const MEADOW_TUFTS = ["meadow-tuft-a", "meadow-tuft-b", "meadow-tuft-c", "meadow-tuft-d", "meadow-tuft-e"]

/** How far a tuft's tip leans at a full gust, in world px: the LPC tufts' (flora.ts SWAY_TILES). */
export const MEADOW_TUFT_LEAN = 2.8

// A scenery pool, resolved. Loud rather than a bare meadow: the art ships with the code that plants it.
const resolved = (ids: string[]): PlantedTileSpec[] =>
    ids.map(id => {
        const planted = compositeTileParent(id)
        if (!planted) throw new Error(`Meadow plant ${id} is not in the content catalog`)
        return planted
    })

/** The tuft pool, resolved. */
export const meadowTufts = (): PlantedTileSpec[] => resolved(MEADOW_TUFTS)

/**
 * The forest-dirt patches' plants (grass.py DIRT_SEDGES / DIRT_HERBS), in place of the LPC sheet's
 * 218 (a broad-bladed sedge) and 219 (a leafy herb) -- three of each, SCENERY like the tufts.
 */
export const DIRT_SEDGES = ["dirt-sedge-a", "dirt-sedge-b", "dirt-sedge-c"]
export const DIRT_HERBS = ["dirt-herb-a", "dirt-herb-b", "dirt-herb-c"]
const LPC_DIRT_SEDGE = 218
const LPC_DIRT_HERB = 219

/**
 * The meadow's version of LPC dirt plant `lpcTilePos` (218 or 219, as the forest-dirt feature draws
 * it), varied by the cell -- so the patch keeps every random draw it made and only the art changes.
 * Anything else comes back as the LPC tile it was.
 */
export const dirtPlantAt = (lpcTilePos: number, x: number, y: number): PlantedTileSpec => {
    const pool = lpcTilePos === LPC_DIRT_SEDGE ? DIRT_SEDGES : lpcTilePos === LPC_DIRT_HERB ? DIRT_HERBS : undefined
    if (!pool) return { tilesetId: FLORA_TILESET, tilePos: lpcTilePos }
    return resolved([pool[Math.floor((cellHash(x + 7919, y) / 2 ** 32) * pool.length)]])[0]
}

let dirtGidsFor: unknown
let dirtGids: Set<number>

/** The gids the meadow's dirt plants are written into a generated room under (tileSetSpecs firstgids). */
export const isDirtPlantGid = (gid: number): boolean => {
    if (dirtGidsFor !== contentCatalog.mapTilesets) {
        dirtGids = new Set(resolved([...DIRT_SEDGES, ...DIRT_HERBS]).map(p => tileSetSpecs[p.tilesetId].firstgid + p.tilePos))
        dirtGidsFor = contentCatalog.mapTilesets
    }
    return dirtGids.has(gid)
}

let tuftIdsFor: unknown
let tuftIds: Set<string>
let leansFor: unknown
let leans: Map<string, number>

/**
 * How far a piece of the meadow's SCENERY leans in a full gust, by the texture id the client draws it
 * under: the tufts and the dirt sedges, which are grass. (The herbs are a stem with leaves, which a
 * shear would turn to rubber; the clumps and the sprout carry a `grass` tag and go by that.)
 */
export const meadowSceneryLean = (textureId: string): number | undefined => {
    if (leansFor !== contentCatalog.mapTilesets) {
        const specs = [...MEADOW_TUFTS, ...DIRT_SEDGES].map(compositeTileParent).filter(Boolean) as PlantedTileSpec[]
        leans = new Map(specs.map(s => [floraTextureId(s.tilesetId, s.tilePos), MEADOW_TUFT_LEAN]))
        leansFor = contentCatalog.mapTilesets
    }
    return leans.get(textureId)
}

/** The texture ids the client draws the meadow's tufts under (see flora.ts floraTextureId). */
export const meadowTuftTextureIds = (): Set<string> => {
    if (tuftIdsFor !== contentCatalog.mapTilesets) {
        const specs = MEADOW_TUFTS.map(compositeTileParent).filter(Boolean) as PlantedTileSpec[]
        tuftIds = new Set(specs.map(s => floraTextureId(s.tilesetId, s.tilePos)))
        tuftIdsFor = contentCatalog.mapTilesets
    }
    return tuftIds
}
